import { Injectable } from '@angular/core';
import { BehaviorSubject, firstValueFrom, map } from 'rxjs';

import { AlertDto, AlertStatus, AlertType, AlertView } from '../models/alerts.models';
import { AlertsApiService } from './alerts-api.service';
import { ProjectContextService } from './project-context.service';
import { SseEventsService } from './sse-events.service';

@Injectable({ providedIn: 'root' })
export class AlertsService {
  private readonly pendingSubject = new BehaviorSubject<AlertDto[]>([]);
  private readonly activeAlertSubject = new BehaviorSubject<AlertDto | null>(null);
  private readonly viewCache = new Map<string, AlertView>();

  readonly alerts$ = this.pendingSubject.asObservable();
  readonly activeAlert$ = this.activeAlertSubject.asObservable();
  readonly activeAlertView$ = this.activeAlert$.pipe(
    map((alert) => (alert ? this.buildView(alert) : null)),
  );

  constructor(
    private readonly api: AlertsApiService,
    private readonly projectContext: ProjectContextService,
    private readonly sse: SseEventsService,
  ) {
    this.projectContext.projectId$.subscribe((projectId) => {
      if (!projectId) {
        this.pendingSubject.next([]);
        this.viewCache.clear();
        this.activeAlertSubject.next(null);
        return;
      }
      void this.loadPending(projectId);
    });

    this.sse.alert$.subscribe((alert) => {
      if (alert.status === 'pending') {
        this.addOrReplace(alert);
      }
    });

    this.sse.alertResolved$.subscribe((resolved) => {
      this.remove(resolved._id);
    });
  }

  get snapshot() {
    return this.pendingSubject.value;
  }

  get activeSnapshot() {
    return this.activeAlertSubject.value;
  }

  get pendingByAsset() {
    const map = new Map<string, AlertDto>();
    for (const alert of this.pendingSubject.value) {
      const existing = map.get(alert.asset_id);
      if (!existing) {
        map.set(alert.asset_id, alert);
        continue;
      }
      if (existing.type !== 'panic' && alert.type === 'panic') {
        map.set(alert.asset_id, alert);
      }
    }
    return map;
  }

  getPendingAlertForAsset(assetId: string) {
    return this.pendingByAsset.get(assetId) ?? null;
  }

  getView(alert: AlertDto): AlertView {
    const cached = this.viewCache.get(alert._id);
    if (cached) return cached;
    const view = this.buildView(alert);
    this.viewCache.set(alert._id, view);
    return view;
  }

  get activeAlertView(): AlertView | null {
    const alert = this.activeAlertSubject.value;
    if (!alert) return null;
    return this.buildView(alert);
  }

  openAlert(alert: AlertDto) {
    this.activeAlertSubject.next(alert);
  }

  closeAlert() {
    this.activeAlertSubject.next(null);
  }

  async resolveAlert(alertId: string, observation?: string) {
    const alert = this.pendingSubject.value.find((a) => a._id === alertId);
    if (!alert) return;

    const updated = await firstValueFrom(this.api.updateStatus(alertId, 'done', observation));
    this.remove(alertId);
    if (this.activeAlertSubject.value?._id === alertId) {
      this.activeAlertSubject.next(updated);
    }
  }

  remove(alertId: string) {
    this.pendingSubject.next(this.pendingSubject.value.filter((a) => a._id !== alertId));
    this.viewCache.delete(alertId);
    if (this.activeAlertSubject.value?._id === alertId) {
      this.activeAlertSubject.next(null);
    }
  }

  private async loadPending(projectId: string) {
    try {
      const pending = await firstValueFrom(this.api.list({ project_id: projectId, status: 'pending' }));
      this.pendingSubject.next(pending ?? []);
      this.viewCache.clear();
    } catch {
      this.pendingSubject.next([]);
      this.viewCache.clear();
    }
  }

  private addOrReplace(alert: AlertDto) {
    const list = this.pendingSubject.value.filter((a) => a._id !== alert._id);
    this.pendingSubject.next([alert, ...list]);
    this.viewCache.delete(alert._id);
  }

  private buildView(alert: AlertDto): AlertView {
    const personName = alert.asset_name ?? alert.asset_id;
    const zoneName = alert.zone_name ?? 'Área não informada';
    const title = this.titleForType(alert.type);
    const severity = this.severityForType(alert.type);
    const timeLabel = this.formatTimeLabel(alert.time_start);
    const timeText = this.formatClock(alert.time_start);
    const message = this.messageForType(alert.type, personName, zoneName, timeText);
    const highlight = `Tempo decorrido: ${this.formatElapsed(alert, false)}`;

    return {
      ...alert,
      title,
      severity,
      personName,
      zoneName,
      timeLabel,
      message,
      highlight,
    };
  }

  private titleForType(type: AlertType) {
    switch (type) {
      case 'panic':
        return 'Pânico acionado';
      case 'batery':
        return 'Alerta de bateria';
      case 'check_time_routine_cold':
      case 'check_time_routine_hot':
      default:
        return 'Alerta de tempo';
    }
  }

  private messageForType(type: AlertType, personName: string, zoneName: string, timeText: string) {
    if (type === 'panic') {
      return `O botão do pânico foi acionado por ${personName} na área “${zoneName}” às ${timeText}`;
    }
    if (type === 'batery') {
      return `Bateria baixa detectada para ${personName} na área “${zoneName}” às ${timeText}`;
    }
    return `Alerta de tempo para ${personName} na área “${zoneName}” às ${timeText}`;
  }

  private severityForType(type: AlertType): AlertView['severity'] {
    if (type === 'panic') return 'danger';
    if (type === 'batery') return 'warning';
    return 'danger';
  }

  private formatTimeLabel(timestamp: number) {
    const diff = Date.now() - timestamp;
    if (diff < 60_000) return 'Agora';
    return this.formatClock(timestamp);
  }

  private formatClock(timestamp: number) {
    const d = new Date(timestamp);
    const hh = String(d.getHours()).padStart(2, '0');
    const mm = String(d.getMinutes()).padStart(2, '0');
    return `${hh}:${mm}`;
  }

  private formatElapsed(alert: AlertDto, includeHours: boolean) {
    const end =
      alert.status === 'done'
        ? alert.time_done ?? alert.time_end ?? Date.now()
        : Date.now();
    const ms = Math.max(0, end - alert.time_start);
    const totalSeconds = Math.floor(ms / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    const parts: string[] = [];
    if (includeHours && hours) parts.push(`${hours}h`);
    if (minutes || (!includeHours && hours)) parts.push(`${minutes}min`);
    parts.push(`${seconds}seg`);
    return parts.join(' ');
  }
}
